import React, { useEffect, useState, useRef } from 'react';
import { Palette, Upload, Trash2, Image as ImageIcon } from 'lucide-react';
import { structuresApi } from '../../utils/api';

const POLICES = [
  { v: 'Helvetica', label: 'Helvetica (sans-serif)', css: 'Helvetica, Arial, sans-serif' },
  { v: 'Times', label: 'Times (serif classique)', css: '"Times New Roman", Times, serif' },
  { v: 'Courier', label: 'Courier (machine à écrire)', css: '"Courier New", Courier, monospace' },
];
const cssFont = (p) => (POLICES.find(x => x.v === p) || POLICES[0]).css;
const FIELDS = ['raison_sociale', 'siret', 'ics', 'siege_adresse', 'nom', 'adresse', 'telephone', 'email', 'couleur_primaire', 'couleur_secondaire', 'police'];
const fromStructure = (s) => {
  const o = {};
  FIELDS.forEach(k => { o[k] = s[k] ?? ''; });
  o.couleur_primaire = s.couleur_primaire || '#16243b';
  o.couleur_secondaire = s.couleur_secondaire || '#c0985a';
  o.police = s.police || 'Helvetica';
  return o;
};

function Field({ label, value, onChange, placeholder }) {
  return (
    <div>
      <label className="text-[12px] text-navy-400">{label}</label>
      <input value={value} onChange={onChange} placeholder={placeholder} className="form-input rounded-xl mt-1" />
    </div>
  );
}

export default function ChartePage() {
  const [structures, setStructures] = useState([]);
  const [selId, setSelId] = useState(null);
  const [form, setForm] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const fileRef = useRef(null);

  const load = () => {
    structuresApi.getAll().then(({ data }) => {
      setStructures(data || []);
      if (data && data.length) selectStructure(data[0]);
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const selectStructure = (s) => {
    setSelId(s.id);
    setForm(fromStructure(s));
    setLogoUrl(s.logo_path ? structuresApi.getLogoUrl(s.id) : null);
    setMsg(null);
  };
  const onSelectId = (id) => {
    const s = structures.find(x => x.id === Number(id));
    if (s) selectStructure(s);
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const { data } = await structuresApi.updateCharte(selId, form);
      setStructures(list => list.map(s => s.id === selId ? data : s));
      setMsg({ ok: true, text: 'Charte enregistrée ✓ Les documents de cette crèche utilisent cette identité.' });
    } catch (e) { setMsg({ ok: false, text: (e.response?.data?.error) || 'Erreur lors de l’enregistrement.' }); }
    finally { setSaving(false); }
  };

  const onLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('logo', file);
    setMsg(null);
    try { await structuresApi.uploadLogo(selId, fd); setLogoUrl(structuresApi.getLogoUrl(selId, Date.now())); }
    catch (err) { setMsg({ ok: false, text: (err.response?.data?.error) || 'Logo refusé (PNG/JPG, 3 Mo max).' }); }
    if (fileRef.current) fileRef.current.value = '';
  };
  const removeLogo = async () => { await structuresApi.deleteLogo(selId); setLogoUrl(null); };

  if (loading || !form) return <div className="py-16 text-center text-navy-300">Chargement…</div>;

  const selNom = structures.find(s => s.id === selId)?.nom || '';

  return (
    <div className="max-w-5xl">
      <div className="mb-5">
        <h1 className="font-serif text-[30px] leading-tight text-navy-900 flex items-center gap-2"><Palette size={26} className="text-gold-600" /> Identité & Charte graphique</h1>
        <div className="w-12 h-0.5 bg-gold-400 rounded-full mt-2 mb-2.5" />
        <p className="text-navy-400 text-[14px]">Chaque crèche a sa <strong>propre identité</strong> (entité juridique, logo, couleurs). Elle s’applique à tous les documents des enfants de cette structure.</p>
      </div>

      {/* Sélecteur de crèche */}
      <div className="flex flex-wrap gap-2 mb-5">
        {structures.map(s => (
          <button key={s.id} onClick={() => selectStructure(s)}
            className={`px-4 py-2 rounded-xl text-[14px] border transition-colors ${selId === s.id ? 'bg-navy-800 text-white border-navy-800' : 'bg-white text-navy-600 border-cream-300 hover:border-navy-300'}`}>
            {s.nom}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-5">
          <div className="apple-card p-5 space-y-3">
            <div className="text-[12px] uppercase tracking-wide text-gold-600 font-semibold">Entité juridique (SAS)</div>
            <Field label="Raison sociale" value={form.raison_sociale} onChange={e => set('raison_sociale', e.target.value)} placeholder="Ex. SAS Les Cachettes" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="SIREN / RCS" value={form.siret} onChange={e => set('siret', e.target.value)} placeholder="881 492 102" />
              <Field label="ICS SEPA" value={form.ics} onChange={e => set('ics', e.target.value)} placeholder="FR12ZZZ…" />
            </div>
            <Field label="Adresse du siège" value={form.siege_adresse} onChange={e => set('siege_adresse', e.target.value)} placeholder="45 Route de Clisson, 44330 Vallet" />
          </div>

          <div className="apple-card p-5 space-y-3">
            <div className="text-[12px] uppercase tracking-wide text-gold-600 font-semibold">Coordonnées de la crèche</div>
            <Field label="Nom de la crèche" value={form.nom} onChange={e => set('nom', e.target.value)} placeholder="La Cachette des Choubidoux" />
            <Field label="Adresse de la crèche" value={form.adresse} onChange={e => set('adresse', e.target.value)} placeholder="3 rue des Tonneliers, 44330 Vallet" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Téléphone" value={form.telephone} onChange={e => set('telephone', e.target.value)} placeholder="02 40 …" />
              <Field label="Email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="contact@…" />
            </div>
          </div>

          <div className="apple-card p-5 space-y-4">
            <div className="text-[12px] uppercase tracking-wide text-gold-600 font-semibold">Couleurs & police</div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[12px] text-navy-400">Couleur principale</label>
                <div className="flex items-center gap-2 mt-1">
                  <input type="color" value={form.couleur_primaire} onChange={e => set('couleur_primaire', e.target.value)} className="w-10 h-10 rounded-lg border border-cream-300 cursor-pointer" />
                  <input value={form.couleur_primaire} onChange={e => set('couleur_primaire', e.target.value)} className="form-input rounded-xl uppercase" />
                </div>
              </div>
              <div>
                <label className="text-[12px] text-navy-400">Couleur secondaire</label>
                <div className="flex items-center gap-2 mt-1">
                  <input type="color" value={form.couleur_secondaire} onChange={e => set('couleur_secondaire', e.target.value)} className="w-10 h-10 rounded-lg border border-cream-300 cursor-pointer" />
                  <input value={form.couleur_secondaire} onChange={e => set('couleur_secondaire', e.target.value)} className="form-input rounded-xl uppercase" />
                </div>
              </div>
            </div>
            <div>
              <label className="text-[12px] text-navy-400">Police des documents</label>
              <select value={form.police} onChange={e => set('police', e.target.value)} className="form-input rounded-xl mt-1">
                {POLICES.map(p => <option key={p.v} value={p.v}>{p.label}</option>)}
              </select>
            </div>
          </div>

          <div className="apple-card p-5">
            <div className="text-[12px] uppercase tracking-wide text-gold-600 font-semibold mb-3">Logo de la crèche</div>
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 rounded-xl border border-dashed border-cream-300 flex items-center justify-center bg-cream-50 overflow-hidden flex-shrink-0">
                {logoUrl ? <img src={logoUrl} alt="logo" className="max-w-full max-h-full object-contain" /> : <ImageIcon size={26} className="text-navy-200" />}
              </div>
              <div className="space-y-2">
                <input ref={fileRef} type="file" accept="image/png,image/jpeg" onChange={onLogo} className="hidden" id="logo-input" />
                <label htmlFor="logo-input" className="apple-btn-secondary inline-flex items-center gap-1.5 cursor-pointer"><Upload size={15} /> Charger un logo</label>
                {logoUrl && <button onClick={removeLogo} className="block text-[13px] text-red-500 hover:text-red-700 flex items-center gap-1"><Trash2 size={14} /> Retirer</button>}
                <p className="text-[11px] text-navy-300">PNG ou JPG, 3 Mo max. Fond transparent recommandé.</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button onClick={save} disabled={saving} className="apple-btn-primary">{saving ? 'Enregistrement…' : `Enregistrer — ${selNom}`}</button>
            {msg && <span className={`text-[13px] ${msg.ok ? 'text-emerald-600' : 'text-red-500'}`}>{msg.text}</span>}
          </div>
        </div>

        {/* Aperçu en direct */}
        <div className="lg:sticky lg:top-4 h-fit">
          <div className="text-[12px] uppercase tracking-wide text-navy-400 font-semibold mb-2">Aperçu d’un document — {selNom}</div>
          <div className="rounded-xl overflow-hidden shadow-lg border border-cream-300 bg-white" style={{ fontFamily: cssFont(form.police) }}>
            <div className="px-5 py-4 flex items-start justify-between" style={{ backgroundColor: form.couleur_primaire }}>
              <div className="flex items-center gap-3 min-w-0">
                {logoUrl && <img src={logoUrl} alt="" className="w-12 h-12 object-contain bg-white/10 rounded" />}
                <div className="min-w-0">
                  <div className="text-white font-bold text-[15px] uppercase truncate">{form.raison_sociale || 'Raison sociale'}</div>
                  <div className="text-white/80 text-[11px] truncate">{form.nom || 'Nom de la crèche'}</div>
                  <div className="text-white/50 text-[10px] truncate">{form.adresse || 'Adresse'}</div>
                </div>
              </div>
              <div className="text-right font-bold text-[15px]" style={{ color: form.couleur_secondaire }}>CONTRAT<br />D’ACCUEIL</div>
            </div>
            <div className="p-5">
              <div className="font-bold text-[13px] pb-1" style={{ color: form.couleur_primaire, borderBottom: `2px solid ${form.couleur_secondaire}` }}>ENTRE LES PARTIES</div>
              <div className="mt-3 space-y-1.5">
                <div className="h-2 rounded bg-navy-100 w-3/4" />
                <div className="h-2 rounded bg-navy-100 w-1/2" />
              </div>
              <div className="font-bold text-[13px] pb-1 mt-5" style={{ color: form.couleur_primaire, borderBottom: `2px solid ${form.couleur_secondaire}` }}>MODALITÉS D’ACCUEIL</div>
              <div className="mt-3 space-y-1.5">
                <div className="h-2 rounded bg-navy-100 w-5/6" />
                <div className="h-2 rounded bg-navy-100 w-2/5" />
              </div>
              <div className="mt-6 pt-2 text-[9px] text-navy-300 text-center border-t border-cream-300">
                {(form.raison_sociale || 'Raison sociale')}{form.siret ? ` — RCS ${form.siret}` : ''} · Document généré par Hello Georges.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
